// DSGVO-konformer Learning-Store: User-Korrekturen + Synonyme.
//
// Alle Daten unter users/{uid}/* — keine cross-user Übermittlung.
// Wird im Import-Review-Screen befüllt und beim nächsten Import gelesen, um
// Few-Shot-Beispiele in den KI-Prompt zu hängen.

import {
  doc, setDoc, collection, query, where, orderBy, limit, getDocs, serverTimestamp,
} from '../lib/fsCompat';
import { db } from '../firebase';

// ── KORREKTUR-ENTRIES ────────────────────────────────────────────────────────

// repairHistory einer TX → kompakter Correction-Eintrag.
// Nur Schema-Wissen (Feld + alt/neu), keine Bulk-Daten.
function summarizeRepairHistory(tx) {
  const history = Array.isArray(tx?.repairHistory) ? tx.repairHistory : [];
  if (!history.length) return null;
  return history.map(h => ({
    field:  h.field,
    oldVal: h.oldVal ?? null,
    newVal: h.newVal ?? null,
    reason: h.reason || null,
  }));
}

// Schreibt User-Korrekturen in users/{uid}/importCorrections/{id}
// Wird vom ImportReviewScreen.handleImport aufgerufen — pro Import EIN Dokument
// mit allen Diffs der bearbeiteten TXs.
export async function saveCorrections({ uid, formatSignature, exchange, transactions }) {
  if (!uid || !transactions?.length) return null;

  const entries = transactions
    .map(tx => {
      const diff = summarizeRepairHistory(tx);
      if (!diff) return null;
      return {
        typ: tx.typ || null,
        sym: tx.sym || tx.coin || null,
        diff,
      };
    })
    .filter(Boolean);

  if (!entries.length) return null;

  try {
    const id = `corr_${Date.now()}`;
    const ref = doc(db, 'users', uid, 'importCorrections', id);
    await setDoc(ref, {
      id,
      formatSignature: formatSignature || null,
      exchange:        exchange || null,
      entries,
      createdAt:       serverTimestamp(),
    });
    return id;
  } catch (err) {
    console.warn('[learningStore] Korrekturen speichern fehlgeschlagen:', err.message);
    return null;
  }
}

// Lädt letzte N Korrekturen für eine Format-Signatur (eigene User-History).
// Wird beim nächsten Import als Few-Shot-Hint in den Prompt eingefügt.
export async function loadRecentCorrections(uid, formatSignature, maxItems = 5) {
  if (!uid || !formatSignature) return [];
  try {
    const q = query(
      collection(db, 'users', uid, 'importCorrections'),
      where('formatSignature', '==', formatSignature),
      orderBy('createdAt', 'desc'),
      limit(maxItems)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data());
  } catch (err) {
    console.warn('[learningStore] Korrekturen lesen fehlgeschlagen:', err.message);
    return [];
  }
}

// ── PER-USER SYNONYME ────────────────────────────────────────────────────────

// Wenn der User einen typ-Wert manuell ändert, wird die Beziehung
// "rawValue → mappedTyp" persistiert. Beim nächsten Import vor der KI-Phase
// als Pre-Mapping angewendet — der User muss die Korrektur nicht erneut machen.
export async function saveTypeSynonym(uid, rawValue, mappedTyp) {
  if (!uid || !rawValue || !mappedTyp) return false;
  const raw = String(rawValue).toLowerCase().trim();
  if (!raw) return false;
  try {
    const slug = raw.replace(/[^a-z0-9]/g, '_').slice(0, 80) || 'syn';
    const ref = doc(db, 'users', uid, 'typeSynonyms', slug);
    await setDoc(ref, {
      raw,
      mapped:    mappedTyp,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (err) {
    console.warn('[learningStore] Synonym speichern fehlgeschlagen:', err.message);
    return false;
  }
}

// Lädt alle Synonyme des Users (kleine Collection, kein Limit nötig).
export async function loadTypeSynonyms(uid) {
  if (!uid) return {};
  try {
    const snap = await getDocs(collection(db, 'users', uid, 'typeSynonyms'));
    const map = {};
    snap.docs.forEach(d => {
      const data = d.data();
      if (data?.raw && data?.mapped) map[data.raw] = data.mapped;
    });
    return map;
  } catch (err) {
    console.warn('[learningStore] Synonyme lesen fehlgeschlagen:', err.message);
    return {};
  }
}
